import { IconAlertCircleFilled } from '@tabler/icons-react';
import NSIcon from 'bricks/NSIcon/NSIcon';
import { useCallback } from 'react';
import {
    DEAL_OVERVIEW_FORM_NAMES,
    DEAL_OVERVIEW_FORMS,
    MEMO_SECTIONS,
    InvestmentMemoNavItem,
} from 'views/InvestmentMemo/constants';
import ExpandableSection from 'components/ExpandableSection/ExpandableSection';
import { InvestmentMemoFormData, SelectedProFormaOption } from 'views/InvestmentMemo/types';
import { AISuggestions } from 'views/InvestmentMemo/hooks/useGenerateInvestmentMemoContent';
import RenderIf from 'components/RenderIf/RenderIf';
import { NSButton } from 'bricks';
import { useInvestmentMemoContext } from 'views/InvestmentMemo/InvestmentMemoContext';
import TeamForm from './TeamForm/TeamForm';
import LocationForm from './LocationForm/LocationForm';
import MarketOverviewForm from './MarketOverviewForm/MarketOverviewForm';
import DevelopmentTimelineForm from './DevelopmentTimelineForm/DevelopmentTimelineForm';
import DealOverviewForm from './DealOverviewForm/DealOverviewForm';
import '../InvestmentMemoSections.scss';

interface InvestmentMemoDealOverviewProps {
    selectedProFormaOption: SelectedProFormaOption;
    formData: InvestmentMemoFormData;
    updateFormData: (section: keyof InvestmentMemoFormData, data: Partial<InvestmentMemoFormData[keyof InvestmentMemoFormData]>) => void;
    navigateToSection: (sideNavName: InvestmentMemoNavItem) => void;
    isGeneratingAIContent?: boolean;
    aiSuggestions: AISuggestions;
}

const InvestmentMemoDealOverview = ({
    selectedProFormaOption,
    formData,
    updateFormData,
    navigateToSection,
    isGeneratingAIContent = false,
    aiSuggestions,
}: InvestmentMemoDealOverviewProps) => {
    const { selectedProForma, isLoadingProForma, hasSectionError } = useInvestmentMemoContext();

    const handleChange = useCallback((section: keyof InvestmentMemoFormData, data: Partial<InvestmentMemoFormData[keyof InvestmentMemoFormData]>) => {
        updateFormData(section, data);
    }, [updateFormData]);

    const handleContinue = () => {
        navigateToSection(InvestmentMemoNavItem.Financials);
    };

    return (
        <div className="d-flex justify-content-center pb-2 px-2">
            <div className="w-75">
                <div>
                    <h2 className="text-dark">{MEMO_SECTIONS[1].TITLE}</h2>
                    <p>
                        {MEMO_SECTIONS[1].DESCRIPTION}
                    </p>
                    <div className="d-flex flex-column mb-2">
                        {DEAL_OVERVIEW_FORMS.map(card => (
                            <div key={card.id}>
                                <ExpandableSection
                                    number={card.number}
                                    title={card.title}
                                    subtitle={card.subtitle}
                                    animate
                                    disableExpandingInReadOnly
                                    isExpanded={Boolean(selectedProForma)}
                                    showWarningIcon={hasSectionError(card.id)}
                                    warningIcon={(
                                        <NSIcon
                                            icon={IconAlertCircleFilled}
                                            size="1x"
                                            style={{ fontSize: '0.9rem' }}
                                            className="text-danger"
                                            data-testid={`section-error-${card.id}`}
                                        />
                                    )}
                                >
                                    <RenderIf
                                        isTrue={Boolean(selectedProForma)}
                                        isLoading={isLoadingProForma}
                                        loadingMessage="Loading Northspyre AI..."
                                        shouldLoadAiLogo
                                    >
                                        <RenderIf isTrue={card.id === DEAL_OVERVIEW_FORM_NAMES.DEAL_OVERVIEW}>
                                            <DealOverviewForm
                                                data={formData.dealOverview}
                                                onChange={data => handleChange('dealOverview', data)}
                                            />
                                        </RenderIf>
                                        <RenderIf isTrue={card.id === DEAL_OVERVIEW_FORM_NAMES.LOCATION}>
                                            <LocationForm
                                                data={formData.location}
                                                onChange={data => updateFormData('location', data)}
                                                isLoading={isGeneratingAIContent}
                                                aiSuggestions={aiSuggestions.drivers}
                                            />
                                        </RenderIf>
                                        <RenderIf isTrue={card.id === DEAL_OVERVIEW_FORM_NAMES.DEVELOPMENT_TIMELINE}>
                                            <DevelopmentTimelineForm
                                                data={formData.developmentTimeline}
                                                onChange={data => handleChange('developmentTimeline', data)}
                                            />
                                        </RenderIf>
                                        <RenderIf isTrue={card.id === DEAL_OVERVIEW_FORM_NAMES.TEAM}>
                                            <TeamForm
                                                data={formData.team}
                                                onChange={data => updateFormData('team', data)}
                                                selectedDealId={selectedProFormaOption.value.dealId}
                                            />
                                        </RenderIf>
                                        <RenderIf isTrue={card.id === DEAL_OVERVIEW_FORM_NAMES.MARKET_OVERVIEW}>
                                            <MarketOverviewForm
                                                data={formData.marketOverview}
                                                onChange={data => updateFormData('marketOverview', data)}
                                                isLoading={isGeneratingAIContent}
                                                aiSuggestions={aiSuggestions.fundamentals}
                                            />
                                        </RenderIf>
                                    </RenderIf>
                                </ExpandableSection>
                            </div>
                        ))}
                    </div>
                    <div className="InvestmentMemoSection__continue-button pt-2 pb-4 position-sticky d-flex justify-content-end">
                        <NSButton
                            callback={handleContinue}
                            color="primary"
                            text="Continue"
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default InvestmentMemoDealOverview;
